import React, { useState, useCallback } from 'react'

import Cards, { Focused } from 'react-credit-cards-2'

import { useNavigate } from 'react-router-dom'

import { Field, useFormikContext } from 'formik'

import { Box, Stack, Typography, MenuItem, Alert, IconButton, InputAdornment, CircularProgress } from '@mui/material'
import { Edit } from '@mui/icons-material'

import { TextInput } from '@/components/Inputs/Text'
import { MaskedInput } from '@/components/Inputs/Masked'
import { SelectInput } from '@/components/Inputs/Select'

import { useToast } from '@/contexts/ToastContext'
import { useAuth } from '@/hooks/useAuth'
import statesData from '@/data/states'
import { useCepSearch } from '@/hooks/useCepSearch'

import 'react-credit-cards-2/dist/es/styles-compiled.css'

import { CardFormData } from './types'

export const CardFormFields: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const toast = useToast()
  const { values, setFieldValue } = useFormikContext<CardFormData>()
  const [focusedField, setFocusedField] = useState<Focused | undefined>(undefined)

  const { searchCep, isPending: isFetchingCep } = useCepSearch({
    onSuccess: (data) => {
      setFieldValue('address_street', data.logradouro)
      setFieldValue('address_neighborhood', data.bairro)
      setFieldValue('address_city', data.localidade)
      setFieldValue('address_state', data.uf)
    },
    onError: (error) => {
      toast.error(error.message || 'Erro ao buscar o CEP')
    },
  })

  const handleCepChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      setFieldValue('address_zip', value)
      searchCep(value)
    },
    [setFieldValue, searchCep]
  )

  return (
    <Stack direction="column" spacing={3} sx={{ width: '100%' }}>
      {!user?.phone ? (
        <Alert
          severity="warning"
          variant="filled"
          action={
            <IconButton color="inherit" onClick={() => navigate('/profile')} sx={{ backgroundColor: 'warning.dark' }}>
              <Edit />
            </IconButton>
          }
          sx={{
            alignItems: 'center',
            '& .MuiAlert-action': { margin: 0, padding: 0 },
          }}
        >
          Você precisa cadastrar um número de telefone para adicionar um cartão.
        </Alert>
      ) : null}

      <Box
        sx={{
          width: '100%',
          '& .rccs': { margin: 0, width: '100% !important' },
          '& .rccs__card': { width: '100% !important', height: 'auto !important', aspectRatio: '1.586' },
        }}
      >
        <Cards
          name={values.name}
          number={values.card_number}
          expiry={values.card_expiration}
          cvc={values.card_cvv}
          focused={focusedField}
          placeholders={{ name: 'NOME DO TITULAR' }}
          locale={{ valid: 'Válido até' }}
        />
      </Box>

      <Field
        component={TextInput}
        placeholder="CPF do Titular"
        name="cpf"
        autoComplete="cc-cpf"
        InputProps={{
          inputComponent: MaskedInput,
          inputProps: {
            inputMode: 'numeric',
            mask: '___.___.___-__',
            replacement: { _: /[0-9]/ },
          },
        }}
        fullWidth
      />

      <Field
        component={TextInput}
        placeholder="Nome do Titular"
        name="name"
        autoComplete="cc-name"
        onFocus={() => setFocusedField('name')}
        onBlur={() => setFocusedField(undefined)}
        fullWidth
      />

      <Field
        component={TextInput}
        placeholder="Número do Cartão"
        name="card_number"
        autoComplete="cc-number"
        InputProps={{
          inputComponent: MaskedInput,
          inputProps: {
            inputMode: 'numeric',
            mask: '____ ____ ____ ____',
            replacement: { _: /[0-9]/ },
          },
        }}
        onFocus={() => setFocusedField('number')}
        onBlur={() => setFocusedField(undefined)}
        fullWidth
      />

      <Field
        component={TextInput}
        placeholder="Data de Validade (MM/AA)"
        name="card_expiration"
        autoComplete="cc-exp"
        InputProps={{
          inputComponent: MaskedInput,
          inputProps: {
            inputMode: 'numeric',
            mask: '__/__',
            replacement: { _: /[0-9]/ },
          },
        }}
        onFocus={() => setFocusedField('expiry')}
        onBlur={() => setFocusedField(undefined)}
        fullWidth
      />

      <Field
        component={TextInput}
        placeholder="Cod. de Segurança (CVV)"
        name="card_cvv"
        autoComplete="cc-csc"
        InputProps={{
          inputComponent: MaskedInput,
          inputProps: {
            inputMode: 'numeric',
            mask: '___',
            replacement: { _: /[0-9]/ },
          },
        }}
        onFocus={() => setFocusedField('cvc')}
        onBlur={() => setFocusedField(undefined)}
        fullWidth
      />

      <Typography variant="subtitle1" fontWeight="bold">
        Endereço de cobrança
      </Typography>

      <Field
        component={TextInput}
        placeholder="CEP"
        name="address_zip"
        autoComplete="address-zip"
        onChange={handleCepChange}
        InputProps={{
          inputComponent: MaskedInput,
          inputProps: {
            inputMode: 'numeric',
            mask: '_____-___',
            replacement: { _: /[0-9]/ },
          },
          endAdornment: isFetchingCep ? (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ) : null,
        }}
        disabled={isFetchingCep}
        fullWidth
      />

      <Field
        component={TextInput}
        placeholder="Endereço"
        name="address_street"
        autoComplete="address-street"
        fullWidth
      />

      <Field
        component={TextInput}
        placeholder="Nº do Endereço"
        type="number"
        name="address_number"
        autoComplete="address-number"
        fullWidth
      />

      <Field
        component={TextInput}
        placeholder="Bairro"
        name="address_neighborhood"
        autoComplete="address-neighborhood"
        fullWidth
      />

      <Field component={TextInput} placeholder="Cidade" name="address_city" autoComplete="address-city" fullWidth />

      <Field
        component={SelectInput}
        name="address_state"
        autoComplete="address-state"
        inputProps={{ maxLength: 2 }}
        displayEmpty
        fullWidth
      >
        <MenuItem value="" disabled>
          <Typography color="text.disabled">Estado</Typography>
        </MenuItem>
        {statesData.map((state) => (
          <MenuItem key={state.abbreviation} value={state.abbreviation}>
            {state.name}
          </MenuItem>
        ))}
      </Field>
    </Stack>
  )
}
